// @ts-nocheck
import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Divider,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Deselect,
  RestartAlt,
  SelectAll,
  ViewColumn,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  setTableColumnVisibility,
  resetTableColumnVisibility,
  initializeTableColumns,
} from "../../store/actions/tableColumnVisibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/**
 * TableColumnManager - A reusable wrapper component for managing table column visibility
 *
 * Features:
 * - Show/hide individual columns
 * - Excel-style column headers (A, B, C, etc.)
 * - Persistent column visibility (Redux)
 * - Show all / Hide all / Reset to defaults
 * - Visual column management interface
 * - State resets on page refresh
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 *   Example: [
 *     { id: 'eventName', label: 'Event Name', defaultVisible: true },
 *     { id: 'eventStart', label: 'Event Start', defaultVisible: true }
 *   ]
 * @param {Function} props.children - Render function that receives column data
 * @param {boolean} props.showExcelHeaders - Show Excel-style column headers (A, B, C, etc.)
 * @param {Function} props.onColumnVisibilityChange - Callback when column visibility changes
 * @param {string} props.tableId - Unique identifier for the table (required for Redux persistence)
 * @param {boolean} props.showToolbar - Show the column manager toolbar
 */
export const TableColumnManager = ({
  columns = [],
  children,
  showExcelHeaders = false,
  onColumnVisibilityChange,
  tableId = "default-table",
  showToolbar = false,
  resetColumnBtnRef = null,
  showUnsavedChanges = false,
  onReset,
}) => {
  const dispatch = useDispatch();
  const { columnVisibility } = useSelector(
    (state) => state.tableColumnVisibility,
  );

  // Get visibility for this specific table from Redux
  const savedTableVisibility = columnVisibility[tableId];
  // Reconcile saved preferences with the columns available to this view/role.
  // Removed IDs must not affect counts; new columns start at their declared default.
  const tableVisibility = useMemo(() => columns.reduce((visibility, column) => {
    visibility[column.id] = typeof savedTableVisibility?.[column.id] === "boolean"
      ? savedTableVisibility[column.id]
      : column.defaultVisible !== false;
    return visibility;
  }, {}), [columns, savedTableVisibility]);

  // Initialize visible columns from Redux or defaults
  const [visibleColumns, setVisibleColumns] = useState(tableVisibility);

  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize Redux state when component mounts or columns change
  useEffect(() => {
    if (!savedTableVisibility) dispatch(initializeTableColumns(tableId, tableVisibility));
    setVisibleColumns(tableVisibility);
  }, [tableId, columns, dispatch, tableVisibility, savedTableVisibility]);

  // Generate Excel-style column letters (A, B, C, ... Z, AA, AB, etc.)
  const getExcelColumnLabel = (index) => {
    let label = "";
    let num = index;
    while (num >= 0) {
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26) - 1;
    }
    return label;
  };

  // Get visible columns in order
  const visibleColumnsList = useMemo(() => {
    return columns.filter((col) => visibleColumns[col.id]);
  }, [columns, visibleColumns]);

  // Handle column visibility toggle
  const handleToggleColumn = (columnId) => {
    const newVisibility = {
      ...visibleColumns,
      [columnId]: !visibleColumns[columnId],
    };

    setVisibleColumns(newVisibility);

    // Dispatch to Redux
    dispatch(setTableColumnVisibility(tableId, newVisibility));

    // Trigger callback
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(newVisibility);
    }
  };

  // Handle show/hide all columns
  const handleToggleAll = (show) => {
    const newVisibility = columns.reduce((acc, col) => {
      acc[col.id] = show;
      return acc;
    }, {});

    setVisibleColumns(newVisibility);

    // Dispatch to Redux
    dispatch(setTableColumnVisibility(tableId, newVisibility));

    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(newVisibility);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    const defaultVisibility = columns.reduce((acc, col) => {
      acc[col.id] = col.defaultVisible !== false;
      return acc;
    }, {});

    setVisibleColumns(defaultVisibility);

    // Dispatch to Redux
    dispatch(resetTableColumnVisibility(tableId, defaultVisibility));

    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(defaultVisibility);
    }

    if (onReset) {
      onReset();
    }
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length;

  return (
    <Box>
      {/* Hidden button to trigger reset from parent via ref */}
      <button
        ref={resetColumnBtnRef}
        style={{ display: "none" }}
        onClick={handleReset}
        type="button"
      />

      {/* Column Manager Toolbar */}
      {showToolbar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.5rem 1rem",
            backgroundColor: "rgba(209, 117, 182, 0.1)",
            gap: "1rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Typography variant="body2" sx={{ color: "#fff" }}>
              Visible Columns: {visibleCount} / {columns.length}
            </Typography>

            {showExcelHeaders && (
              <Chip
                size="small"
                label="Excel Headers"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                }}
              />
            )}

            {showUnsavedChanges && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  backgroundColor: alpha("#ff9800", 0.15),
                  color: "#ffa726",
                  borderRadius: "4px",
                  padding: "2px 8px",
                  fontSize: "1rem",
                  ml: "0.25rem",
                }}
                role="alert"
                aria-live="polite"
              >
                <WarningAmberIcon sx={{ fontSize: "1rem" }} />
                Click save edits to save changes
              </Box>
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumn />}
            onClick={handleOpenMenu}
            sx={{
              borderColor: "#D175B6",
              color: "#fff",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(209, 117, 182, 0.2)",
              },
            }}
          >
            Manage Columns
          </Button>
        </Box>
      )}

      {/* Column Visibility Menu */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            border: "1px solid #D175B6",
            borderRadius: "8px",
            minWidth: "300px",
            maxHeight: "500px",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
            Column Visibility
          </Typography>

          {/* Show Excel Headers Toggle */}
          {/* <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showExcelHeaders}
                  disabled
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#D175B6',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#D175B6',
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Show Excel Headers (A, B, C...)
                </Typography>
              }
            />
          </Box> */}

          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mb: 2 }} />

          {/* Quick Actions */}
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Tooltip title="Show All">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleToggleAll(true)}
                sx={{
                  flex: 1,
                  borderColor: "#D175B6",
                  color: "#fff",
                  fontSize: "0.75rem",
                }}
              >
                <SelectAll />
              </Button>
            </Tooltip>
            <Tooltip title="Hide All">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleToggleAll(false)}
                sx={{
                  flex: 1,
                  borderColor: "#D175B6",
                  color: "#fff",
                  fontSize: "0.75rem",
                }}
              >
                <Deselect />
              </Button>
            </Tooltip>
            <Tooltip title="Reset">
              <Button
                size="small"
                variant="outlined"
                onClick={handleReset}
                sx={{
                  flex: 1,
                  borderColor: "#D175B6",
                  color: "#fff",
                  fontSize: "0.75rem",
                }}
              >
                <RestartAlt />
              </Button>
            </Tooltip>
          </Box>

          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mb: 2 }} />

          {/* Column List */}
          <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
            {columns.map((column, index) => (
              <Box
                key={column.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(209, 117, 182, 0.1)",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleColumns[column.id] || false}
                      onChange={() => handleToggleColumn(column.id)}
                      sx={{
                        color: "#D175B6",
                        "&.Mui-checked": {
                          color: "#D175B6",
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {showExcelHeaders && (
                        <Chip
                          size="small"
                          label={getExcelColumnLabel(index)}
                          sx={{
                            backgroundColor: "rgba(209, 117, 182, 0.3)",
                            color: "#fff",
                            fontSize: "0.7rem",
                            height: "20px",
                            minWidth: "28px",
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ color: "#fff" }}>
                        {column.label}
                      </Typography>
                    </Box>
                  }
                />
                <IconButton
                  size="small"
                  sx={{ color: "rgba(255, 255, 255, 0.5)", cursor: "default" }}
                >
                  {visibleColumns[column.id] ? (
                    <Visibility fontSize="small" />
                  ) : (
                    <VisibilityOff fontSize="small" />
                  )}
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Render children with visible columns data */}
      {typeof children === "function"
        ? children({
            visibleColumns: visibleColumnsList,
            visibilityMap: visibleColumns,
            getExcelColumnLabel,
            showExcelHeaders,
            allColumns: columns,
          })
        : children}
    </Box>
  );
};

TableColumnManager.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      defaultVisible: PropTypes.bool,
    }),
  ),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  showExcelHeaders: PropTypes.bool,
  onColumnVisibilityChange: PropTypes.func,
  tableId: PropTypes.string,
  showToolbar: PropTypes.bool,
  resetColumnBtnRef: PropTypes.object,
  onReset: PropTypes.func,
  showUnsavedChanges: PropTypes.bool,
};

export default TableColumnManager;
